import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

const backgroundImage = 'https://images.unsplash.com/photo-1600989930246-829897a96389?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';

export default function AdminLogin() {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { authenticated, loading, login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!loading && authenticated) return <Navigate to="/admin" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await login(password);
      navigate(location.state?.from || '/admin', { replace: true });
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Administrator access could not be verified.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-[#FAF8F5] lg:grid-cols-[1.1fr_0.9fr]" data-testid="admin-login-page">
      <section className="relative hidden overflow-hidden lg:block">
        <img src={backgroundImage} alt="Abstract forest green forms" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#16372D]/45" />
        <div className="absolute inset-x-0 bottom-0 p-12 text-white">
          <p className="text-xs font-semibold uppercase text-white/60">Prosperitas operations</p>
          <h1 className="mt-4 max-w-xl font-serif text-5xl leading-tight">See the conversation behind every number.</h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/75">Health, engagement, conversions and coaching activity in one secure console.</p>
        </div>
      </section>
      <section className="flex items-center px-6 py-16 sm:px-14 lg:px-20">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="mb-12 flex h-12 w-12 items-center justify-center bg-[#2C5545] text-white rounded-sm"><LockKeyhole className="h-5 w-5" strokeWidth={1.5} /></div>
          <p className="text-xs font-semibold uppercase text-[#D26046]">Private access</p>
          <h2 className="mt-3 font-serif text-4xl text-[#1A1C18]">Administrator login</h2>
          <p className="mt-3 text-sm leading-relaxed text-[#6B6E69]">Enter the administrator password to continue.</p>
          <label className="mt-10 block text-xs font-semibold uppercase text-[#4A4D4A]" htmlFor="admin-password">Password</label>
          <div className="relative mt-2">
            <input id="admin-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required className="h-14 w-full border border-[#CFC8BD] bg-white px-4 pr-12 text-base outline-none transition-colors focus:border-[#2C5545] focus:ring-2 focus:ring-[#2C5545]/15 rounded-sm" data-testid="admin-password-input" />
            <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-[#8A8D8A] hover:text-[#2C5545]" aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="admin-password-toggle">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          {error && <div className="mt-4 border-l-4 border-[#D26046] bg-[#FBEDE8] px-4 py-3 text-sm text-[#8A3B27]" role="alert" data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={submitting || !password} className="mt-6 h-12 w-full bg-[#2C5545] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1E3F33] disabled:cursor-not-allowed disabled:opacity-50 rounded-sm" data-testid="admin-login-submit-button">{submitting ? 'Verifying…' : 'Enter operations console'}</button>
          <p className="mt-8 text-xs leading-relaxed text-[#8A8D8A]">Sensitive transcript access is recorded in the administrator audit log.</p>
        </form>
      </section>
    </div>
  );
}
